import { useState, useEffect } from 'react';
import { aiListModels, aiCheckReceipt, aiAcceptSuggestion } from '../services/api';
import { X, Robot, Shield, Check, Warning, Sparkle, Lock, ArrowRight, Info } from '@phosphor-icons/react';

const DEFAULT_CONFIG = {
  send_ocr_text: true,
  send_keywords: true,
  send_amount: true,
  send_date: true,
  send_kostenstellen_list: true,
  send_property_type: true,
  send_mwst: false,
  send_category: false,
};

const FIELD_LABELS = [
  { key: 'send_ocr_text', label: 'Anonymisierter OCR-Text', recommended: true, desc: 'Beleg-Text mit ersetzten PII ([FIRMA], [STRASSE], etc.)' },
  { key: 'send_keywords', label: 'Stichwörter', recommended: true, desc: 'z.B. „Heizung", „Wasser", „Müll"' },
  { key: 'send_amount', label: 'Gesamtbetrag', recommended: true, desc: 'Numerischer Wert ohne Empfänger' },
  { key: 'send_date', label: 'Beleg-Datum', recommended: true, desc: 'YYYY-MM-DD ohne Zeitstempel' },
  { key: 'send_kostenstellen_list', label: 'Liste Ihrer Kostenstellen', recommended: true, desc: 'Damit KI nur aus IHREN Kategorien wählt' },
  { key: 'send_property_type', label: 'Immobilien-Typ', recommended: true, desc: 'z.B. „apartment_building" – OHNE Name/Adresse' },
  { key: 'send_mwst', label: 'MwSt.-Satz', recommended: false, desc: '19%, 7% oder 0% (heuristisch erkannt)' },
  { key: 'send_category', label: 'Beleg-Typ', recommended: false, desc: 'Falls schon manuell vergeben (z.B. „Rechnung")' },
];

export default function AICheckModal({ receipt, kostenstellen, onClose, onAccepted }) {
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [configured, setConfigured] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [phase, setPhase] = useState('config'); // config | loading | result | accepted | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptProperty, setAcceptProperty] = useState(true);

  useEffect(() => {
    aiListModels().then(res => {
      setModels(res.data.models || []);
      setDefaultModel(res.data.default);
      setSelectedModel(res.data.default);
      setConfigured(res.data.configured);
    }).catch(err => {
      setError(err.response?.data?.detail || 'Konnte Modelle nicht laden');
    });
  }, []);

  const toggleField = (key) => {
    setConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRun = async () => {
    setPhase('loading');
    setError('');
    try {
      const res = await aiCheckReceipt(receipt.id, { ...config, model: selectedModel });
      setResult(res.data);
      setPhase('result');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'KI-Aufruf fehlgeschlagen');
      setPhase('error');
    }
  };

  const handleAccept = async () => {
    if (!result?.suggestion?.kostenstelle_id) return;
    setAccepting(true);
    try {
      // Use accept-full to include property_id if user wants it
      const body = {
        kostenstelle_id: result.suggestion.kostenstelle_id,
        umlagefaehig: !!result.suggestion.umlagefaehig,
      };
      if (acceptProperty && result.property_suggestion?.property_id) {
        body.property_id = result.property_suggestion.property_id;
      }
      const url = body.property_id
        ? `/ai-audit-logs/${result.audit_id}/accept-full`
        : `/ai-audit-logs/${result.audit_id}/accept`;
      const { default: api } = await import('../services/api');
      await api.post(url, body);
      setPhase('accepted');
      setTimeout(() => {
        onAccepted?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Übernahme fehlgeschlagen');
      setPhase('error');
    } finally {
      setAccepting(false);
    }
  };

  const piiCount = result?.pii_counts_removed ? Object.values(result.pii_counts_removed).reduce((a, b) => a + b, 0) : 0;

  const getKsName = (id) => {
    const ks = kostenstellen?.find(k => k.id === id);
    return ks ? ks.name : '–';
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="ai-check-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-blue-50 sticky top-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 flex items-center justify-center">
              <Robot weight="bold" className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">KI-Beleg-Prüfung</h2>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Shield weight="bold" className="w-3 h-3" />
                IONOS AI Model Hub (Server in Frankfurt, DSGVO-konform)
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600" data-testid="ai-close-button">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* CONFIG PHASE */}
          {phase === 'config' && (
            <>
              {!configured && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 mb-4 flex items-start gap-2" data-testid="ai-not-configured-warning">
                  <Warning weight="bold" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">IONOS API-Token nicht konfiguriert</p>
                    <p className="text-xs mt-1">Bitte tragen Sie <code className="font-mono bg-amber-100 px-1">IONOS_API_KEY</code> in <code className="font-mono bg-amber-100 px-1">/app/backend/.env</code> ein und starten Sie das Backend neu.</p>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">KI-Modell</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                  data-testid="ai-model-select"
                >
                  {models.map(m => (
                    <option key={m.id} value={m.id}>{m.label}{m.id === defaultModel ? ' • Standard' : ''}</option>
                  ))}
                </select>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 p-4 mb-4 flex items-start gap-2">
                <Lock weight="bold" className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-medium">Diese Daten werden NIEMALS gesendet:</p>
                  <p className="text-xs mt-1">Namen, Adressen, Firmen, IBAN, Telefon, E-Mail, Steuernummern, Property-Name, Mieter-Daten</p>
                </div>
              </div>

              <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Welche Daten sollen zur Klassifizierung gesendet werden?</h3>
              <div className="space-y-2 mb-6">
                {FIELD_LABELS.map(field => (
                  <label
                    key={field.key}
                    className={`flex items-start gap-3 p-3 border cursor-pointer hover:bg-slate-50 ${config[field.key] ? 'border-violet-300 bg-violet-50/40' : 'border-gray-200'}`}
                  >
                    <input
                      type="checkbox"
                      checked={config[field.key]}
                      onChange={() => toggleField(field.key)}
                      className="mt-1 w-4 h-4 accent-violet-700"
                      data-testid={`ai-field-${field.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm flex items-center gap-2">
                        {field.label}
                        {field.recommended && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">empfohlen</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{field.desc}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-gray-300 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleRun}
                  disabled={!configured}
                  className="flex-1 bg-violet-700 text-white py-2.5 font-medium hover:bg-violet-800 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="ai-run-button"
                >
                  <Sparkle weight="bold" className="w-4 h-4" />
                  KI-Prüfung starten
                </button>
              </div>
            </>
          )}

          {/* LOADING */}
          {phase === 'loading' && (
            <div className="flex flex-col items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700 mb-4"></div>
              <p className="font-medium text-slate-900">KI prüft den Beleg...</p>
              <p className="text-sm text-slate-500 mt-1">Anonymisierte Daten werden an IONOS gesendet</p>
            </div>
          )}

          {/* RESULT */}
          {phase === 'result' && result && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 p-4 mb-4 flex items-start gap-3" data-testid="ai-result-summary">
                <Shield weight="bold" className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-medium">{piiCount > 0 ? `${piiCount} persönliche Daten entfernt` : 'Keine PII gefunden'}</p>
                  <p className="text-xs mt-1">
                    Entfernt: {Object.entries(result.pii_counts_removed || {}).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'}
                  </p>
                </div>
              </div>

              {result.suggestion ? (
                <div className="border border-violet-200 bg-violet-50 p-5 mb-4">
                  <p className="text-xs uppercase tracking-widest font-bold text-violet-700 mb-2">KI-Vorschlag</p>
                  <h3 className="font-heading text-2xl font-black text-slate-900 mb-1" data-testid="ai-suggested-kostenstelle">
                    {result.suggestion.kostenstelle_name || getKsName(result.suggestion.kostenstelle_id)}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 font-medium ${result.suggestion.umlagefaehig ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {result.suggestion.umlagefaehig ? '✓ Umlagefähig' : '⚠ Nicht umlagefähig'}
                    </span>
                    {typeof result.suggestion.konfidenz === 'number' && (
                      <span className="text-xs text-slate-600">
                        Konfidenz: <span className="font-mono font-bold">{Math.round(result.suggestion.konfidenz * 100)}%</span>
                      </span>
                    )}
                  </div>
                  {result.suggestion.begruendung && (
                    <p className="text-sm text-slate-700 italic">„{result.suggestion.begruendung}"</p>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 p-4 mb-4">
                  <p className="text-sm text-amber-800">KI konnte keine gültige Antwort liefern. Rohausgabe siehe unten.</p>
                </div>
              )}

              {/* Property suggestion (local match - DSGVO safe) */}
              {result.property_suggestion?.property_id && (
                <div className="border border-blue-200 bg-blue-50 p-4 mb-4" data-testid="ai-property-suggestion">
                  <div className="flex items-start gap-3">
                    <Shield weight="bold" className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-widest font-bold text-blue-700">Immobilie erkannt (lokal)</p>
                      <p className="font-medium text-slate-900 mt-1">{result.property_suggestion.property_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{result.property_suggestion.note}</p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptProperty}
                        onChange={(e) => setAcceptProperty(e.target.checked)}
                        className="w-4 h-4 accent-blue-700"
                        data-testid="ai-accept-property"
                      />
                      <span className="text-xs font-medium text-slate-700">übernehmen</span>
                    </label>
                  </div>
                </div>
              )}

              <details className="mb-4 border border-gray-200">
                <summary className="cursor-pointer px-4 py-2 bg-gray-50 text-sm font-medium text-slate-700 hover:bg-gray-100">
                  <Info weight="bold" className="w-3 h-3 inline mr-1" />
                  Gesendete Daten anzeigen (für DSGVO-Nachweis)
                </summary>
                <pre className="p-4 bg-white text-xs overflow-x-auto whitespace-pre-wrap font-mono text-slate-700" data-testid="ai-sent-payload">
                  {JSON.stringify(result.sent_payload, null, 2)}
                </pre>
              </details>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setPhase('config')}
                  className="flex-1 border border-gray-300 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
                  data-testid="ai-back-button"
                >
                  Zurück
                </button>
                <button
                  onClick={handleAccept}
                  disabled={!result.suggestion?.kostenstelle_id || accepting}
                  className="flex-1 bg-violet-700 text-white py-2.5 font-medium hover:bg-violet-800 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="ai-accept-button"
                >
                  <Check weight="bold" className="w-4 h-4" />
                  {accepting ? 'Übernehme...' : 'Vorschlag übernehmen'}
                </button>
              </div>
            </>
          )}

          {/* ACCEPTED */}
          {phase === 'accepted' && (
            <div className="flex flex-col items-center py-12" data-testid="ai-accepted-screen">
              <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center mb-4">
                <Check weight="bold" className="w-8 h-8 text-emerald-700" />
              </div>
              <p className="font-heading text-xl font-bold text-slate-900">Übernommen</p>
              <p className="text-sm text-slate-500 mt-1">Beleg wurde der Kostenstelle zugeordnet</p>
            </div>
          )}

          {/* ERROR */}
          {phase === 'error' && (
            <div className="py-8">
              <div className="bg-red-50 border border-red-200 p-4 text-red-800" data-testid="ai-error">
                <p className="font-medium">Fehler</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setPhase('config')}
                  className="flex-1 border border-gray-300 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
                >
                  Zurück
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
